// src/components/congressional/congressShared.jsx
// Congressional Trade Tracker — shared constants/helpers for CongressPage.jsx's
// four tab components (TradeFeedTab, MemberProfilesTab, CommitteeViewTab,
// InsightsTab). Extracted per the SAME decision made for the backend split
// (backend/utils/congressShared.js) — four files needing the same small set
// of display constants and helpers, an actively growing feature set, real
// risk of the copies drifting apart if duplicated a fourth time. A shared
// module, not duplication, for consistency across both halves of this split.

// DEFAULT_PAGE_SIZE (owner request): was 25, now 10 — keeps a page's first
// view short by default; the new page-size control (ConFilterBar.jsx) lets a
// member choose 10/25/50/100 for themselves. Renamed from PAGE_SIZE to make
// clear this is only the STARTING value, not a fixed constant every tab must
// use — each tab now tracks its own pageSize as state.
export const DEFAULT_PAGE_SIZE = 10;
export const PARTY_COLORS = { D: '#00d4ff', R: '#ef4444', I: '#a78bfa' };
export const TYPE_COLORS = { Purchase: '#00ff88', Sale: '#ef4444', Exchange: '#f59e0b', Hold: '#64748b' };

// OWNER conviction multiplier — same values congressTrades.js's own moat-data
// comment documents (SELF=1.0 · JOINT=0.85 · SPOUSE=0.70 · DEPENDENT=0.60).
export const OWNER_LABELS = {
  SELF: 'Self (1.0x conviction)',
  JOINT: 'Joint (0.85x conviction)',
  SPOUSE: 'Spouse (0.70x conviction)',
  DEPENDENT: 'Dependent (0.60x conviction)',
  OTHER: 'Other',
};

// Same fix as the original CongressPage.jsx's own fmtDate (real confirmed
// bug): a bare 'YYYY-MM-DD' string parsed via `new Date()` is treated as UTC
// midnight, then toLocaleDateString() renders it in the browser's LOCAL
// timezone — silently shifting the displayed date back one day for any US
// timezone. Parsed directly from the string's own digits instead, so this
// can't happen regardless of viewer timezone.
const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
export const fmtDate = (d) => {
  if (!d) return '—';
  const m = String(d).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '—';
  return `${parseInt(m[3], 10)} ${MONTH_ABBR[parseInt(m[2], 10) - 1]} ${m[1]}`;
};

// FIXED (real bug, confirmed via owner screenshot + code trace): Card only ever
// destructured { children, style } — any onClick passed to <Card onClick={...}>
// (MemberProfilesTab.jsx does exactly this, on every row) was silently dropped
// before reaching the actual <div>, so every member row was unclickable,
// uniformly, regardless of whether that member had any trades — matching the
// "irrespective of trades or not" symptom exactly. CommitteeViewTab never hit
// this because its onClick sits on an inner div inside Card's children, not on
// Card itself. Fixed by accepting and forwarding onClick here, once, in the
// shared component — fixes every current and future consumer, not just
// MemberProfilesTab.jsx.
export const Card = ({ children, style, onClick }) => (
  <div onClick={onClick} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '1rem 1.25rem', marginBottom: '0.75rem', ...style }}>{children}</div>
);
export const inputStyle = { padding: '0.5rem 0.75rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: '#f1f5f9', fontSize: '0.82rem', boxSizing: 'border-box' };
export const smallBtn = (color) => ({ padding: '0.4rem 0.9rem', background: color + '1a', border: '1px solid ' + color + '4d', borderRadius: '6px', color, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 });
export const authHeaders = (token) => (token ? { Authorization: 'Bearer ' + token } : {});

// CATEGORY_GROUP_MAP — reverse lookup, raw ASSETCATEGORY value → display
// group key. Mirrors backend/utils/congressShared.js's CATEGORY_GROUPS
// exactly (that file is authoritative for the real, allowlist-confirmed
// values; this is the frontend's classification lookup for grouping
// ALREADY-FETCHED trades client-side — used by the Member Detail Subpage's
// Purchase/Sale × category display, owner request).
export const CATEGORY_GROUP_MAP = {
  stock: 'stocks_options',
  option: 'stocks_options',
  fund: 'funds',
  tbill: 'bonds_tbills',
  bond: 'bonds_tbills',
  crypto: 'crypto_commodities',
  commodity: 'crypto_commodities',
};
export const CATEGORY_GROUP_LABELS = {
  stocks_options: 'Stocks & Options',
  funds: 'Funds',
  bonds_tbills: 'Bonds & T-Bills',
  crypto_commodities: 'Crypto & Commodities',
};
// Display order — Stocks & Options first (the largest group for most
// members), Crypto & Commodities last (smallest/rarest in practice).
export const CATEGORY_GROUP_ORDER = ['stocks_options', 'funds', 'bonds_tbills', 'crypto_commodities'];
